import type { BundleTranscriptSegment } from '@/features/sessions/types';

import type { LiveTranscriptSegment } from '../hooks/useSessionTranscript';

interface MergeSavedTranscriptInput {
  live: LiveTranscriptSegment[];
  saved: BundleTranscriptSegment[];
  clientUserId: string | null | undefined;
  practitionerUserId: string | null | undefined;
}

/**
 * The live panel only hears lines spoken after this tab joined, so after a
 * rejoin or reload it would start empty. Lines the agent already saved are
 * shown first, in startMs order, followed by the live ones. Saved and live
 * copies of a line share the segment id, so a line is never shown twice.
 */
export function mergeSavedTranscript({
  live,
  saved,
  clientUserId,
  practitionerUserId,
}: MergeSavedTranscriptInput): LiveTranscriptSegment[] {
  if (saved.length === 0) return live;
  const liveIds = new Set(live.map((segment) => segment.id));
  const identityFor = (speakerRole: string): string => {
    if (speakerRole === 'client') return clientUserId ?? '';
    if (speakerRole === 'practitioner') return practitionerUserId ?? '';
    return '';
  };
  const earlier = saved
    .filter((segment) => !liveIds.has(segment.segmentId))
    .sort((first, second) => first.startMs - second.startMs)
    .map(
      (segment): LiveTranscriptSegment => ({
        id: segment.segmentId,
        text: segment.text,
        language: segment.language,
        startMs: segment.startMs,
        endMs: segment.endMs,
        final: true,
        participantIdentity: identityFor(segment.speakerRole),
        participantName: '',
        receivedAt: 0,
      }),
    );
  return [...earlier, ...live];
}
